import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import portalService from '../../services/portalService';
import portalAdminService from '../../services/portalAdminService';
import { useAuth, useUser, useDialog } from '../../context';
import PortalShell from './PortalShell';
import PortalInvitesPanel from './PortalInvitesPanel';
import PortalCityPicker from './PortalCityPicker';
import PortalLocationPicker, { detectMyLocation } from './PortalLocationPicker';
import GiveTakeBoard from '../../components/GiveTakeBoard';
import Linkify from '../../components/Linkify';

/**
 * /portal/home — accepted-member home. Hero "send a signal" + Signal Wall
 * with city filter + sidebar (placecard, cities, members-in-your-cities).
 */
export default function PortalHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useUser();
  const { showConfirm } = useDialog();
  const [signals, setSignals] = useState([]);
  const [mySignals, setMySignals] = useState([]);
  const [myCities, setMyCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [membersNearMe, setMembersNearMe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myLocation, setMyLocation] = useState(null); // { latitude, longitude, label, radiusMiles }
  const [keyTier, setKeyTier] = useState('member'); // 'member' (5 keys) | 'key_holder' (unlimited)
  // Feed layout state (matches the prototype: filter dropdown + collapsible panels)
  const [cityFilter, setCityFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [citiesOpen, setCitiesOpen] = useState(true);
  const [cityQ, setCityQ] = useState('');

  // Server is the authoritative source of "is this person a Portal Noir member?"
  // Trusting the stale `user.isPortalMember` from the AuthContext payload
  // caused a redirect loop with /portal whenever the user's local session
  // pre-dated the IsPortalMember field being added to the auth DTO
  // (everyone logged in before that deploy had `undefined` here).
  useEffect(() => {
    let cancelled = false;
    portalService.getState().then((r) => {
      if (cancelled) return;
      if (r.success && r.data?.state !== 'accepted') {
        navigate('/noir', { replace: true });
      } else if (r.success) {
        setKeyTier(r.data?.keyTier || 'member');
      }
    });
    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => {
    portalService.listCities().then((r) => { if (r.success) setAllCities(r.data || []); });
    portalService.getMyCities().then((r) => {
      if (r.success) setMyCities(r.data || []);
    });
    portalService.getMyLocation().then(async (r) => {
      if (!r.success) return;
      setMyLocation(r.data || null);
      // Auto-detect on first visit when we have no saved location. The
      // browser will show the permission prompt; if the user denies we
      // record that in sessionStorage so re-mounts don't re-ask.
      if (!r.data?.label) {
        const detected = await detectMyLocation();
        if (detected) {
          await saveLocation(detected);
        }
      }
    });
  // saveLocation is stable enough — pulling it in causes a re-run loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    // City filter is now derived server-side from the viewer's current
    // location (50mi Haversine). The list endpoint accepts cities for
    // legacy callers but ignores them when a portal location is set.
    portalService.listSignals([]).then((r) => {
      if (r.success) setSignals(r.data || []);
      setLoading(false);
    });
  }, [myLocation?.label]);

  // Signals you're part of (hosting / opted-in / invited) — any distance.
  useEffect(() => {
    portalService.listMySignals().then((r) => { if (r.success) setMySignals(r.data || []); });
  }, []);

  // Members in my cities — list (admin endpoint works for any portal admin; for now non-admin members we just show empty)
  useEffect(() => {
    if (!myCities.length) { setMembersNearMe([]); return; }
    portalAdminService.listMembers({ city: myCities[0], pageSize: 5 }).then((r) => {
      if (r.success) setMembersNearMe((r.data || []).filter((m) => m.userId !== user?.id));
    }).catch(() => setMembersNearMe([]));
  }, [myCities, user]);

  const saveCities = async (next) => {
    setMyCities(next);
    await portalService.setMyCities(next);
  };

  const saveLocation = async ({ lat, lng, label }) => {
    const r = await portalService.setMyLocation({ latitude: lat, longitude: lng, label });
    if (r.success) setMyLocation(r.data);
  };

  const removeSignal = async (signal) => {
    const ok = await showConfirm({
      title: 'Delete this Signal?',
      message: "It disappears from everyone's feed for good. This can't be undone.",
      confirmLabel: 'DELETE',
      cancelLabel: 'KEEP IT',
      variant: 'danger',
    });
    if (!ok) return;
    const r = await portalService.deleteSignal(signal.eventId);
    if (r.success) {
      setSignals((prev) => prev.filter((s) => s.eventId !== signal.eventId));
      setMySignals((prev) => prev.filter((s) => s.eventId !== signal.eventId));
    }
  };

  // Distinct cities present in the loaded feed → the filter dropdown options.
  const feedCities = Array.from(new Set(signals.map((s) => s.primaryCityLabel).filter(Boolean)));
  const shownSignals = cityFilter === 'all' ? signals : signals.filter((s) => s.primaryCityLabel === cityFilter);
  const cityMatches = cityQ.trim()
    ? allCities.filter((c) => c.label.toLowerCase().includes(cityQ.toLowerCase()) && !myCities.includes(c.slug))
    : [];

  return (
    <PortalShell viewerState="accepted">
      <div className="wrap" style={{ paddingTop: 'clamp(40px,7vw,80px)', paddingBottom: 100 }}>
        {/* hero */}
        <section className="hero enter">
          <div className="hero__kick">
            <span className="live" />
            <span className="kicker kicker--gold">LIVE IN YOUR CITIES</span>
          </div>
          <h1 className="display" style={{ fontSize: 'clamp(42px,7.2vw,96px)' }}>Find your people<span className="dot">.</span></h1>
          <p className="lede" style={{ marginTop: 30, maxWidth: '30ch' }}>A network of remarkable people.</p>
        </section>

        {/* compose */}
        <button className="compose enter enter-2" onClick={() => navigate('/noir/signals/new')}>
          <span className="compose__q">Want company?</span>
          <span className="btn btn--gold">SEND OUT A SIGNAL <span className="arr">→</span></span>
        </button>

        {/* Signals you're part of — hosting, joined, or invited (any distance).
            Upcoming shown; past ones tucked behind a collapsible section. */}
        {(() => {
          // Past signals are intentionally not shown — only upcoming ones you're part of.
          const nowMs = Date.now();
          const upcoming = mySignals.filter((s) => new Date(s.dateTimeUtc).getTime() >= nowMs);
          const card = (s) => (
            <SignalCard
              key={s.eventId}
              signal={s}
              onOpen={() => navigate(`/noir/signals/${s.eventId}`)}
              onEdit={() => navigate(`/noir/signals/${s.eventId}/edit`)}
              onDelete={() => removeSignal(s)}
            />
          );
          if (upcoming.length === 0) return null;
          return (
            <div className="enter enter-2" style={{ marginBottom: 34 }}>
              <div className="secthead" style={{ marginBottom: 14 }}>
                <span className="kicker kicker--gold">YOUR SIGNALS</span>
                <span className="meta">{upcoming.length}</span>
              </div>
              <div className="feedlist">{upcoming.map(card)}</div>
            </div>
          );
        })()}

        {/* feed */}
        <div className="secthead enter enter-2">
          <span className="kicker">SIGNALS</span>
          <div className="dropdown">
            <button className="dropbtn" onClick={() => setFilterOpen((o) => !o)}>
              <span>{cityFilter === 'all' ? 'ALL CITIES' : cityFilter.toUpperCase()}</span>
              <span className={'chev' + (filterOpen ? '' : ' is-closed')}>▾</span>
            </button>
            {filterOpen && (
              <div className="dropmenu">
                <button className={'dropitem' + (cityFilter === 'all' ? ' is-on' : '')} onClick={() => { setCityFilter('all'); setFilterOpen(false); }}>ALL CITIES</button>
                {feedCities.map((c) => (
                  <button key={c} className={'dropitem' + (cityFilter === c ? ' is-on' : '')} onClick={() => { setCityFilter(c); setFilterOpen(false); }}>{c.toUpperCase()}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Where you are — collapses to the current location + a CHANGE button
            once set (search hidden); shows search only while setting/changing. */}
        <div className="enter enter-3" style={{ marginTop: 22, marginBottom: 16 }}>
          <PortalLocationPicker
            current={myLocation?.label ? { label: myLocation.label, lat: myLocation.latitude, lng: myLocation.longitude } : null}
            onChange={saveLocation}
            radiusMiles={myLocation?.radiusMiles || 50}
          />
        </div>

        <div className="feedlist enter enter-3">
          {loading ? (
            <p className="meta" style={{ padding: '22px 2px', color: 'var(--cream-faint)' }}>LOADING…</p>
          ) : !myLocation?.label ? (
            <p className="lede" style={{ padding: '26px 2px', color: 'var(--cream-faint)' }}>Set where you are above to see Signals within 50mi.</p>
          ) : shownSignals.length === 0 ? (
            <p className="lede" style={{ padding: '26px 2px', color: 'var(--cream-faint)' }}>Nothing live near you yet. Be the one who starts something.</p>
          ) : (
            shownSignals.map((s) => <SignalCard key={s.eventId} signal={s} onOpen={() => navigate(`/noir/signals/${s.eventId}`)} onEdit={() => navigate(`/noir/signals/${s.eventId}/edit`)} onDelete={() => removeSignal(s)} />)
          )}
        </div>

        {/* give / take board */}
        {myLocation?.label && (
          <section className="gtsection enter enter-3" style={{ marginTop: 64 }}>
            <div className="secthead" style={{ marginBottom: 8 }}>
              <span className="kicker">GIVE / TAKE BOARD</span>
              <button className="alink" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => navigate('/noir/give-take')}>OPEN THE BOARD →</button>
            </div>
            <p className="gtsubhead">Posts clear after 24 hours, or the moment they’re claimed.</p>
            <GiveTakeBoard context="portal" variant="portal" />
          </section>
        )}

        {/* secondary: cities + keys */}
        <div className="subgrid enter enter-4">
          {/* Your Cities */}
          <div className="subpanel">
            <button className="subpanel__toggle" onClick={() => setCitiesOpen((o) => !o)}>
              <span className="kicker">YOUR CITIES</span>
              <span className="right">
                <span className="meta" style={{ color: 'var(--cream-faint)' }}>{myCities.length} ON</span>
                <span className={'chev' + (citiesOpen ? '' : ' is-closed')}>▾</span>
              </span>
            </button>
            {citiesOpen && (
              <div className="subbody enter">
                <p className="lede" style={{ fontSize: 17, marginBottom: 18 }}>The cities you keep an eye on. Signals from these land in your feed.</p>
                {myCities.length > 0 && (
                  <div className="filterrow" style={{ marginBottom: 14 }}>
                    {myCities.map((slug) => {
                      const label = allCities.find((c) => c.slug === slug)?.label || slug;
                      return (
                        <button key={slug} className="chip chip--on" onClick={() => saveCities(myCities.filter((x) => x !== slug))}>
                          {label.toUpperCase()} <span className="x">×</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <input className="input" placeholder="Search any city…" value={cityQ} onChange={(e) => setCityQ(e.target.value)} />
                {cityMatches.length > 0 && (
                  <div className="filterrow" style={{ marginTop: 12 }}>
                    {cityMatches.slice(0, 6).map((c) => (
                      <button key={c.slug} className="chip" onClick={() => { saveCities([...myCities, c.slug]); setCityQ(''); }}>+ {c.label.toUpperCase()}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Keys — real invite logic lives in PortalInvitesPanel (already matches the copy/states) */}
          <PortalInvitesPanel keyTier={keyTier} />
        </div>
      </div>
    </PortalShell>
  );
}

function Capacity({ joined, cap }) {
  const pct = Math.min(100, (joined / cap) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 96, height: 1, background: 'var(--line)', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, width: pct + '%', height: 1, background: 'var(--gold)' }} />
      </div>
      <span className="meta" style={{ letterSpacing: '0.12em' }}>{joined} OF {cap} IN</span>
    </div>
  );
}

function SignalCard({ signal, onOpen, onEdit, onDelete }) {
  const init = (signal.hostDisplayName || '?').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <article className="sigcard" onClick={onOpen}>
      <span className="sigcard__seam" />
      <div className="sigcard__top">
        {signal.hostAvatarUrl
          ? <img className="ava" src={signal.hostAvatarUrl} alt="" />
          : <span className="ava" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--cream-dim)' }}>{init}</span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sigcard__name">
            {signal.hostDisplayName} {signal.hostHandle && <span className="sigcard__handle">@{signal.hostHandle}</span>}
          </div>
          <div className="meta" style={{ marginTop: 3 }}>
            {signal.primaryCityLabel} &nbsp;·&nbsp; {formatDateAgo(signal.dateTimeUtc)}
          </div>
        </div>
        <span className={'tag ' + (signal.mode === 'curated' ? 'tag--cur' : 'tag--open')}>
          {signal.mode === 'curated' ? 'CURATED' : 'OPEN'}
        </span>
      </div>

      {signal.venueName && (
        <p className="portal-serif" style={{ margin: '14px 0 0', fontSize: 16, color: 'var(--cream-dim)', fontStyle: 'italic' }}>
          <span style={{ color: 'var(--gold)', fontStyle: 'normal' }}>@</span> {signal.venueName}
        </p>
      )}

      <p className="sigcard__note">“<Linkify text={signal.signalText} linkClassName="portal-gold" linkStyle={{ textDecoration: 'underline' }} />”</p>

      <div className="sigcard__foot">
        {signal.capacitySlots > 0
          ? <Capacity joined={signal.acceptedCount} cap={signal.capacitySlots} />
          : <span className="meta" style={{ color: 'var(--cream-faint)' }}>OPEN ROOM</span>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          {signal.costCents > 0
            ? <span className="meta">${(signal.costCents / 100).toFixed(0)} / HEAD</span>
            : <span className="meta" style={{ color: 'var(--cream-faint)' }}>NO COVER</span>}
          {signal.isMine
            ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
                <span className="alink" style={{ color: 'var(--cream-dim)' }}>YOURS&nbsp;·&nbsp;OPEN</span>
                {onEdit && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                    className="meta"
                    style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', letterSpacing: '0.12em', padding: 0 }}
                  >
                    EDIT
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="meta"
                    style={{ background: 'none', border: 'none', color: '#B4736A', cursor: 'pointer', letterSpacing: '0.12em', padding: 0 }}
                  >
                    DELETE
                  </button>
                )}
              </span>
            )
            : signal.viewerState === 'accepted'
              ? <span className="alink">OPEN CHAT →</span>
              : signal.viewerState === 'applied_pending'
                ? <span className="meta" style={{ color: 'var(--cream-faint)' }}>WAITING ON HOST</span>
                : signal.isFull
                  ? <span className="meta" style={{ color: 'var(--cream-faint)' }}>FULL</span>
                  : <span className="alink">{signal.mode === 'curated' ? 'ASK TO COME →' : 'I’M IN →'}</span>}
        </div>
      </div>
    </article>
  );
}

function formatDateAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const ms = d.getTime() - Date.now();
  const days = Math.round(ms / (24 * 3600 * 1000));
  if (days === 0) return 'TODAY';
  if (days === 1) return 'TOMORROW';
  if (days > 0 && days < 7) return `IN ${days}D`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
}
